import type { ReactNode } from "react";

/**
 * Generic, reusable form-information component.
 *
 * This replaces QuotationInformation.tsx, InvoiceInformation.tsx and
 * DeliveryOrderInformation.tsx. It contains ONLY presentation logic —
 * business logic (state, validation, API calls, submit logic) stays in
 * each page. Pages describe what fields to render via `fields`, and this
 * component renders them in the existing two-column layout.
 *
 * Validation display (red border + inline message) is also purely
 * presentational here: pages compute the actual error messages (see
 * src/helpers/validation.ts) and just pass them in via `errors`.
 *
 * To support a new module (Purchase Order, Customer, Supplier, Sales, ...)
 * a page just needs to declare its own value shape + field config; no new
 * wrapper component is needed.
 */

export type DocumentFieldType =
  | "text"
  | "email"
  | "date"
  | "select"
  | "readonly"
  | "currency";

export interface DocumentFieldOption {
  value: number | string;
  label: string;
}

export interface DocumentFieldConfig<T> {
  /** Key of the value object this field reads/writes */
  key: keyof T;
  /** Label rendered to the left of the field */
  label: string;
  /** Which kind of control to render */
  type: DocumentFieldType;
  /** Which of the two columns this field belongs to */
  column: 1 | 2;
  /** Placeholder text for free-text inputs */
  placeholder?: string;
  /** Options for "select" fields */
  options?: DocumentFieldOption[];
  /** Optional first/placeholder option for "select" fields (e.g. "Select Sales") */
  placeholderOption?: DocumentFieldOption;
  /** When true, the raw string from the input is converted to a Number before onChange is called */
  numeric?: boolean;
  /** Fallback text for "readonly" fields when the value is empty (defaults to "Auto-generated") */
  readOnlyFallback?: string;
}

interface DocumentInformationProps<T> {
  value: T;
  fields: DocumentFieldConfig<T>[];
  onChange: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Tailwind width class applied to every field label. Defaults to "w-36". */
  labelWidthClass?: string;
  /** Validation errors keyed by field name (same keys as `T`). Renders a red border + message below the field. */
  errors?: Record<string, string>;
}

const baseInputClass =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400";

const errorInputClass =
  "w-full rounded-lg border border-red-400 px-3 py-2 text-sm outline-none focus:border-red-500";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(value || 0);

export default function DocumentInformation<T extends Record<string, any>>({
  value,
  fields,
  onChange,
  labelWidthClass = "w-36",
  errors,
}: DocumentInformationProps<T>) {
  const leftFields = fields.filter((field) => field.column === 1);
  const rightFields = fields.filter((field) => field.column === 2);

  const renderField = (field: DocumentFieldConfig<T>) => {
    const rawValue = value[field.key];
    const fieldId = `field-${String(field.key)}`;
    const hasError = Boolean(errors?.[field.key as string]);
    const inputClass = hasError ? errorInputClass : baseInputClass;

    switch (field.type) {
      case "readonly":
        return (
          <input
            id={fieldId}
            type="text"
            value={(rawValue as string) ?? field.readOnlyFallback ?? "Auto-generated"}
            disabled
            className={`${baseInputClass} bg-gray-50 text-gray-400`}
          />
        );

      case "currency":
        return (
          <input
            id={fieldId}
            type="text"
            value={formatCurrency(Number(rawValue) || 0)}
            disabled
            className={`${baseInputClass} bg-gray-50 text-gray-400`}
          />
        );

      case "select":
        return (
          <select
            id={fieldId}
            value={rawValue === null || rawValue === undefined ? "" : rawValue}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = field.numeric
                ? raw === ""
                  ? ("" as unknown as T[typeof field.key])
                  : (Number(raw) as unknown as T[typeof field.key])
                : (raw as unknown as T[typeof field.key]);
              onChange(field.key, parsed);
            }}
            className={inputClass}
          >
            {field.placeholderOption && (
              <option value={field.placeholderOption.value}>
                {field.placeholderOption.label}
              </option>
            )}
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case "date":
        return (
          <input
            id={fieldId}
            type="date"
            value={(rawValue as string) ?? ""}
            onChange={(e) =>
              onChange(field.key, e.target.value as unknown as T[typeof field.key])
            }
            className={inputClass}
          />
        );

      case "email":
        return (
          <input
            id={fieldId}
            type="email"
            value={(rawValue as string) ?? ""}
            onChange={(e) =>
              onChange(field.key, e.target.value as unknown as T[typeof field.key])
            }
            placeholder={field.placeholder ?? "Input text"}
            className={inputClass}
          />
        );

      case "text":
      default:
        return (
          <input
            id={fieldId}
            type="text"
            value={(rawValue as string) ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = field.numeric
                ? (Number(raw) as unknown as T[typeof field.key])
                : (raw as unknown as T[typeof field.key]);
              onChange(field.key, parsed);
            }}
            placeholder={field.placeholder ?? "Input text"}
            className={inputClass}
          />
        );
    }
  };

  return (
    <div className="grid grid-cols-2 gap-x-10 gap-y-4">
      <div className="space-y-4">
        {leftFields.map((field) => (
          <Field
            key={String(field.key)}
            label={field.label}
            labelWidthClass={labelWidthClass}
            error={errors?.[field.key as string]}
          >
            {renderField(field)}
          </Field>
        ))}
      </div>
      <div className="space-y-4">
        {rightFields.map((field) => (
          <Field
            key={String(field.key)}
            label={field.label}
            labelWidthClass={labelWidthClass}
            error={errors?.[field.key as string]}
          >
            {renderField(field)}
          </Field>
        ))}
      </div>
    </div>
  );
}

function Field({
  label,
  labelWidthClass,
  error,
  children,
}: {
  label: string;
  labelWidthClass: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-4">
      <label className={`${labelWidthClass} shrink-0 pt-2 text-sm text-gray-600`}>{label}</label>
      <div className="flex-1">
        {children}
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    </div>
  );
}
